import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { Shell } from './Shell';
import { RequireAuth, RequireModule } from '../auth/guards';
import { HOME_PATH, MODULES, type ModuleDef } from '../config/modules';
import { PageSkeleton } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { PlaceholderPage } from '../features/placeholders/PlaceholderPage';

// Built screens are code-split; placeholders are tiny and stay in the main chunk.
const ExecutivePage = lazy(() =>
  import('../features/executive/ExecutivePage').then((m) => ({ default: m.ExecutivePage })),
);
const SalesPage = lazy(() => import('../features/sales/SalesPage').then((m) => ({ default: m.SalesPage })));
// Dev gallery: the condition is replaced at build time, so production builds drop the chunk.
const GalleryPage = import.meta.env.DEV
  ? lazy(() => import('../features/dev/GalleryPage').then((m) => ({ default: m.GalleryPage })))
  : null;

const SCREENS: Record<string, () => React.JSX.Element> = {
  executive: () => <ExecutivePage />,
  sales: () => <SalesPage />,
};

function screen(m: ModuleDef) {
  const render = SCREENS[m.key];
  return (
    <RequireModule moduleKey={m.key}>
      <Suspense fallback={<PageSkeleton />}>
        {render && m.status === 'ready' ? render() : <PlaceholderPage mod={m} />}
      </Suspense>
    </RequireModule>
  );
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to={HOME_PATH} replace /> },
      ...MODULES.flatMap((m) => [
        { path: m.path.slice(1), element: screen(m) },
        ...(m.tabs ? [{ path: `${m.path.slice(1)}/:tab`, element: screen(m) }] : []),
      ]),
      ...(GalleryPage
        ? [
            {
              path: 'dev/gallery',
              element: (
                <Suspense fallback={<PageSkeleton />}>
                  <GalleryPage />
                </Suspense>
              ),
            },
          ]
        : []),
      {
        path: '*',
        element: <EmptyState title="Page not found" text="Use the sidebar or press Ctrl+K to find a page." />,
      },
    ],
  },
]);
